import React from "react";
import { Check, User, Clock, PartyPopper } from "lucide-react";
import { cn } from "@/lib/utils";

interface TripProgressTrackerProps {
  currentStep: 0 | 1 | 2 | 3 | 4;
  className?: string;
}

const TripProgressTracker = ({ currentStep, className }: TripProgressTrackerProps) => {
  const steps = [
    { id: 1, label: "Trip Booked", icon: Check },
    { id: 2, beforeLabel: "Confirming Driver", afterLabel: "Driver Confirmed", icon: User },
    { id: 3, beforeLabel: "Driver On-Route", afterLabel: "Driver Arrived", icon: Clock },
    { id: 4, beforeLabel: "Trip In-Progress", afterLabel: "Trip Completed", icon: PartyPopper },
  ];

  return (
    <div className={cn("w-full py-4", className)}>
      <div className="relative">
        {/* Progress Line Background */}
        <div className="absolute top-6 left-8 right-8 h-0.5 bg-gray-200 rounded-full" />
        
        {/* Animated Progress Line */}
        <div 
          className="absolute top-6 left-8 h-0.5 gradient-bg rounded-full transition-all duration-1000 ease-out"
          style={{ 
            width: `calc(${((currentStep - 1) / 3) * 100}% - 1rem)`,
          }}
        />

        {/* Steps */}
        <div className="relative flex justify-between">
          {steps.map((step, index) => {
            const isCompleted = step.id <= currentStep;
            const isActive = step.id === currentStep;
            const Icon = step.icon;

            return (
              <div key={step.id} className="flex flex-col items-center">
                {/* Step Circle */}
                <div
                  className={cn(
                    "relative w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-500 ease-out",
                    isCompleted
                      ? "gradient-bg border-transparent text-black shadow-lg scale-110"
                      : "bg-white border-gray-300 text-gray-400"
                  )}
                  style={{
                    transitionDelay: `${index * 100}ms`,
                  }}
                >
                  {/* Tracing Animation for Active Step */}
                  {isActive && (
                    <div className="absolute inset-0 rounded-full">
                      <div className="absolute inset-0 rounded-full border-2 border-transparent bg-gradient-to-r from-green-400 to-blue-500 animate-spin">
                        <div className="absolute inset-1 bg-white rounded-full" />
                      </div>
                    </div>
                  )}
                  
                  <Icon 
                    size={18} 
                    className={cn(
                      "relative z-10 transition-all duration-300",
                      isCompleted && "animate-scale-in"
                    )} 
                  />
                </div>

                {/* Step Label */}
                <div className="mt-3 text-center">
                   <p 
                     className={cn(
                       "text-xs font-medium transition-colors duration-300",
                       isCompleted ? "text-gray-900" : "text-gray-500"
                     )}
                   >
                     {step.id === 1 
                       ? step.label 
                       : isCompleted 
                         ? step.afterLabel 
                         : step.beforeLabel
                     }
                   </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TripProgressTracker;